import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  FunctionParameters as FunctionParametersType,
  FunctionParameter,
} from "../models/ExternalAgent";

interface FunctionParametersProps {
  title: string;
  parameters: FunctionParametersType;
  onChange: (parameters: FunctionParametersType) => void;
  className?: string;
  id?: string;
}

export function FunctionParameters({
  title,
  parameters,
  onChange,
  className,
  id = "function-params",
}: FunctionParametersProps) {
  const parametersList: FunctionParameter[] = Object.entries(
    parameters.properties,
  ).map(([name, param]) => ({
    name,
    type: param.type as "string" | "number" | "boolean",
    description: param.description,
    required: parameters.required.includes(name),
    min: param.min,
    max: param.max,
  }));

  const updateParameters = (newParametersList: FunctionParameter[]) => {
    const newParameters: FunctionParametersType = {
      properties: {},
      required: [],
      type: "object",
    };

    newParametersList.forEach((param) => {
      newParameters.properties[param.name] = {
        type: param.type,
        description: param.description,
        ...(param.min !== undefined && { min: param.min }),
        ...(param.max !== undefined && { max: param.max }),
      };

      if (param.required) {
        newParameters.required.push(param.name);
      }
    });

    onChange(newParameters);
  };

  const addParameter = () => {
    const newParam: FunctionParameter = {
      name: "",
      type: "string",
      description: "",
      required: false,
    };
    updateParameters([...parametersList, newParam]);
  };

  const removeParameter = (index: number) => {
    const newList = parametersList.filter((_, i) => i !== index);
    updateParameters(newList);
  };

  const updateParameter = (
    index: number,
    field: keyof FunctionParameter,
    value: any,
  ) => {
    const newList = [...parametersList];
    newList[index] = { ...newList[index], [field]: value };
    updateParameters(newList);
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          {title}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addParameter}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Parameter
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {parametersList.length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            No parameters defined. Add at least one parameter.
          </p>
        ) : (
          parametersList.map((param, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Parameter {index + 1}</h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeParameter(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`${id}-param-name-${index}`}>Name *</Label>
                  <Input
                    id={`${id}-param-name-${index}`}
                    value={param.name}
                    onChange={(e) =>
                      updateParameter(index, "name", e.target.value)
                    }
                    placeholder="Parameter name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor={`${id}-param-type-${index}`}>Type *</Label>
                  <Select
                    value={param.type}
                    onValueChange={(value) =>
                      updateParameter(index, "type", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="string">String</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="boolean">Boolean</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor={`${id}-param-description-${index}`}>
                  Description *
                </Label>
                <Textarea
                  id={`${id}-param-description-${index}`}
                  value={param.description}
                  onChange={(e) =>
                    updateParameter(index, "description", e.target.value)
                  }
                  placeholder="Parameter description"
                  rows={2}
                  required
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`${id}-param-required-${index}`}
                  checked={param.required}
                  onCheckedChange={(checked) =>
                    updateParameter(index, "required", checked)
                  }
                />
                <Label htmlFor={`${id}-param-required-${index}`}>
                  Required
                </Label>
              </div>

              {param.type === "number" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`${id}-param-min-${index}`}>
                      Min (optional)
                    </Label>
                    <Input
                      id={`${id}-param-min-${index}`}
                      type="number"
                      value={param.min || ""}
                      onChange={(e) =>
                        updateParameter(
                          index,
                          "min",
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
                      placeholder="Minimum value"
                    />
                  </div>

                  <div>
                    <Label htmlFor={`${id}-param-max-${index}`}>
                      Max (optional)
                    </Label>
                    <Input
                      id={`${id}-param-max-${index}`}
                      type="number"
                      value={param.max || ""}
                      onChange={(e) =>
                        updateParameter(
                          index,
                          "max",
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
                      placeholder="Maximum value"
                    />
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
